<script setup>
import i18n from "@/plugins/i18n";
import {defineEmits, defineProps, nextTick, onMounted, ref, reactive,  watch} from "vue";
import {useLocales} from "@/composables/locales";
const {locale} = useLocales()

const props =defineProps({
  id: { type: String, default: 'date-range-picker' },
  withAllTime: { type: Boolean, default: false },
  start:{ type: String, default: '' },
  end:{ type: String, default: '' },
})

const emits = defineEmits(['update:date'])

const range = ref({
  start_date: '',
  end_date: ''
})
const allTimeStart = ref(moment('1900-01-01T00:00:00'))
const allTimeEnd = ref(moment('2100-12-31T23:59:59'))
const options = reactive({
  startDate: '',
  endDate: '',
  opens: 'left',
  applyClass: 'btn-primary',
  cancelClass: 'btn-light-primary',
  locale: {
    format: 'DD/MM/YYYY',
    separator: ' - ',
    applyLabel: i18n.global.t('apply'),
    cancelLabel: i18n.global.t('cancel'),
    fromLabel: i18n.global.t('from'),
    toLabel: i18n.global.t('to'),
    customRangeLabel: i18n.global.t('custom_range'),
    weekLabel: 'S',
    daysOfWeek: [i18n.global.t('di'), i18n.global.t('lu'), i18n.global.t('ma'), i18n.global.t('me'), i18n.global.t('je'), i18n.global.t('ve'), i18n.global.t('sa')],
    monthNames: [i18n.global.t('january'), i18n.global.t('february'), i18n.global.t('march'), i18n.global.t('april'), i18n.global.t('may'),
      i18n.global.t('june'), i18n.global.t('july'), i18n.global.t('august'), i18n.global.t('september'), i18n.global.t('october'), i18n.global.t('november'), i18n.global.t('december')],
    firstDay: 1
  },
  ranges: {
    [i18n.global.t('today')]: [moment(), moment()],
    [i18n.global.t('yesterday')]: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
    [i18n.global.t('last7Days')]: [moment().subtract(6, 'days'), moment()],
    [i18n.global.t('last30Days')]: [moment().subtract(29, 'days'), moment()],
    [i18n.global.t('thisMonth')]: [moment().startOf('month'), moment().endOf('month')],
    [i18n.global.t('lastMonth')]: [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
  }
})

watch(() => locale.value, async (value) => {
  await nextTick()
  moment.locale(locale.value)

  options.locale = {
    format: 'DD/MM/YYYY',
    separator: ' - ',
    applyLabel: i18n.global.t('apply'),
    cancelLabel: i18n.global.t('cancel'),
    fromLabel: i18n.global.t('from'),
    toLabel: i18n.global.t('to'),
    customRangeLabel: i18n.global.t('custom_range'),
    weekLabel: 'S',
    daysOfWeek: [
      i18n.global.t('di'), i18n.global.t('lu'), i18n.global.t('ma'),
      i18n.global.t('me'), i18n.global.t('je'), i18n.global.t('ve'), i18n.global.t('sa')
    ],
    monthNames: [
      i18n.global.t('january'), i18n.global.t('february'), i18n.global.t('march'),
      i18n.global.t('april'), i18n.global.t('may'), i18n.global.t('june'),
      i18n.global.t('july'), i18n.global.t('august'), i18n.global.t('september'),
      i18n.global.t('october'), i18n.global.t('november'), i18n.global.t('december')
    ],
    firstDay: 1
  }

  options.ranges = {
    [i18n.global.t('today')]: [moment(), moment()],
    [i18n.global.t('yesterday')]: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
    [i18n.global.t('last7Days')]: [moment().subtract(6, 'days'), moment()],
    [i18n.global.t('last30Days')]: [moment().subtract(29, 'days'), moment()],
    [i18n.global.t('thisMonth')]: [moment().startOf('month'), moment().endOf('month')],
    [i18n.global.t('lastMonth')]: [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
  }

  if (props.withAllTime) {
    options.ranges = {
      [i18n.global.t('allTime')]: [allTimeStart.value, allTimeEnd.value],
      ...options.ranges
    }
  }

  initDateRange() 
}, { deep: true })

function initDateRange() {
  const element = document.querySelector('#' + props.id);
  const $el = $(element);

  const existing = $el.data('daterangepicker');
  if (existing) {
    existing.remove(); 
    $el.off();
  }

  let start, end;

  if (props.start && props.end) {
    start = moment(props.start, 'DD-MM-YYYY');
    end = moment(props.end, 'DD-MM-YYYY');
  }else if(props.withAllTime) {
    options.ranges = {[i18n.global.t('allTime')]: [allTimeStart.value, allTimeEnd.value], ...options.ranges,};
    start = allTimeStart.value;
    end = allTimeEnd.value;
  } else {
    start = moment().subtract(29, 'days');
    end = moment();
    range.value.start_date = start.format('DD-MM-YYYY')
    range.value.end_date = end.format('DD-MM-YYYY')
  }

  options.startDate = start
  options.endDate = end

  emits('update:date', range.value)

  $(element).daterangepicker(options, cb);

  cb(start, end);
}

function cb(start, end) {
  let element = document.querySelector('#' + props.id);
  let display = element?.querySelector('div');
  if (display) {
    if(normalizeMoment(start).toISOString() === normalizeMoment(allTimeStart.value).toISOString()
        && normalizeMoment(end).toISOString() === normalizeMoment(allTimeEnd.value).toISOString()) {
      display.innerHTML = i18n.global.t('allTime');
      range.value.start_date = ''
      range.value.end_date = ''
    } else {
      moment.locale(locale.value);
      display.innerHTML = start.format('D MMM YYYY') + ' - ' + end.format('D MMM YYYY');
      range.value.start_date = start.format('DD-MM-YYYY')
      range.value.end_date = end.format('DD-MM-YYYY')
    }
    emits('update:date', range.value)
  }
}

function normalizeMoment(date) {
  return moment(date)
      .milliseconds(0)
      .seconds(0)
      .minutes(0)
      .hours(0);
}

onMounted(() => {
  nextTick(() => {
    moment.locale(locale.value);
    initDateRange()
  })
})

</script>

<template>
  <div :id="id" data-kt-daterangepicker-opens="left"
       class="btn btn-sm btn-withe h-35px d-flex mx-5 rounded-3 align-items-center px-4 shadow-sm">
    <i class="fas fa-calendar mx-5 text-primary ms-2 me-0"></i>
    <div class="text-primary mx-2 fw-bolder">
      {{ $t('loadingDateRange') }}...
    </div>
    <i class="fas fa-chevron-down mx-2 text-primary"></i>
  </div>
</template>

<style scoped>
</style>
